/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the dispatch dialog and create the resulting run.
 * @state-owner activities UI store holds the assignment draft
 * @allowed-imports React; same-module store and mutations; dataset and workspace stores; shared components and hooks; active context
 * @forbidden direct API client; student-only surfaces
 * @public-api activities module
 * @side-effects creates a run and navigates to the resulting screen
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect } from 'react';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { ModalDocument, OptionCard, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { findActivity } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { AssistantPicker, defaultAssistant } from '../../assistants';
import { useAssignActivity } from '../api/activities.mutations';
import { useActivityUiStore } from '../store/activity-ui-store';

export function AssignmentDialog({
  activityId,
  activityIds,
  classIndex,
  preselected,
}: {
  activityId: string;
  /** Several materials going out together; they share one audience and one mode. */
  readonly activityIds?: readonly string[];
  classIndex?: number;
  preselected?: readonly string[];
}) {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const draft = useActivityUiStore((state) => state.assignment);
  const setAssignment = useActivityUiStore((state) => state.setAssignment);
  const patchAssignment = useActivityUiStore((state) => state.patchAssignment);
  const activity = findActivity(activities, activityId);
  const assignActivity = useAssignActivity();
  const contextId = useActiveContextId();
  const defaultClassIndex = classIndex ?? activity?.usedByClassIndexes[0] ?? 0;

  useEffect(() => {
    setAssignment({
      activityId,
      classIndex: defaultClassIndex,
      pickClass: classIndex == null,
      audience: preselected?.length ? 'some' : 'all',
      students: preselected ?? [],
      mode: 'live',
      assistantId: null,
    });
  }, [activityId, classIndex, defaultClassIndex, preselected, setAssignment]);

  if (!draft || !activity) return null;
  const classroom = classes[draft.classIndex];

  /** and issues the dispatch command outside demo mode. */
  const dispatch = () => {
    const store = useDatasetStore.getState();
    const targets = activityIds?.length ? activityIds : [draft.activityId];
    let id = '';
    for (const target of targets) {
      assignActivity.mutate({
        activityId: target,
        classId: classes[draft.classIndex].id,
        contextId,
        mode: draft.mode === 'live' ? 'live' : 'homework',
      });
      id = store.allocateRunId();
      store.prependRun({
        id,
        activityId: target,
        classIndex: draft.classIndex,
        mode: draft.mode,
        status: draft.mode === 'live' ? 'live' : 'open',
        dateLabel: t('今天'),
        audience: draft.audience,
        students: [...draft.students],
        assistantId: draft.assistantId ?? defaultAssistant(store.assistants)?.id,
      });
    }
    closeModal();
    if (draft.mode === 'live') {
      navigate('live', id);
      notify(`已發放：${classes[draft.classIndex].name} 開始作答`);
      return;
    }
    navigate('room', `c${draft.classIndex}`, { classTab: 'act', activityTab: 'run' });
    notify(t('已指派為作業，截止今晚 23:59'));
  };

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button className="bt pri" type="button" onClick={dispatch}>
            {draft.mode === 'live' ? '開始課堂進行' : t('指派為作業')}
          </button>
        </>
      }
      maxWidth={560}
      subtitle={
        activityIds && activityIds.length > 1
          ? `${t('共')} ${activityIds.length} ${t('項教材')} · ${classroom.name}`
          : `${activity.name} · ${classroom.name}`
      }
      title={t('發放')}
    >
      {draft.pickClass ? (
        <>
          <SectionLabel first>{t('班級')}</SectionLabel>
          <div className="fbar" style={{ margin: '0 0 8px' }}>
            {classes.map((item, index) => (
              <button
                className={`ch ${draft.classIndex === index ? 'on' : ''}`}
                key={item.id}
                type="button"
                onClick={() => patchAssignment({ classIndex: index })}
              >
                {item.name}
              </button>
            ))}
          </div>
        </>
      ) : null}
      <SectionLabel first={!draft.pickClass}>{t('對象')}</SectionLabel>
      <OptionCard
        description={`${classroom.students.length} 位學生`}
        on={draft.audience === 'all'}
        onSelect={() => patchAssignment({ audience: 'all' })}
        title={t('整個班級')}
      />
      {draft.students.length ? (
        <OptionCard
          description={draft.students.join('、')}
          on={draft.audience === 'some'}
          onSelect={() => patchAssignment({ audience: 'some' })}
          title={t('指定學生')}
        />
      ) : null}
      <SectionLabel>{t('方式')}</SectionLabel>
      <OptionCard
        description={t('現在帶著上。即時面板會顯示每個人在哪一題，卡住的排進介入佇列。')}
        on={draft.mode === 'live'}
        onSelect={() => patchAssignment({ mode: 'live' })}
        title={t('課堂進行')}
      />
      <OptionCard
        description={t('學生自己找時間寫，不會出現在即時面板，到截止再收。')}
        on={draft.mode === 'static'}
        onSelect={() => patchAssignment({ mode: 'static' })}
        title={t('指派作業')}
      />
      <SectionLabel>{t('AI 助教')}</SectionLabel>
      <AssistantPicker
        onSelect={(assistantId) => patchAssignment({ assistantId })}
        selectedId={draft.assistantId}
      />
    </ModalDocument>
  );
}
